import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import content from "@/content";
import Spotlight from "./Spotlight";

const EASE = [0.22, 1, 0.36, 1];

const lookbookCtx = require.context("../assets/lookbook", false, /\.(jpe?g|png|webp)$/i);
let thumbCtx = null;
try {
  thumbCtx = require.context("../assets/lookbook/thumbs", false, /\.(jpe?g|png|webp)$/i);
} catch {
  thumbCtx = null;
}

function naturalSort(a, b) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

function getThumb(file) {
  if (!thumbCtx) return null;
  const key = "./" + file.replace(/\.[^.]+$/, ".webp");
  return thumbCtx.keys().includes(key) ? thumbCtx(key) : null;
}

function buildLooks() {
  const cards = new Map();
  lookbookCtx.keys().map((k) => k.replace("./", "")).sort(naturalSort).forEach((file) => {
    const base = file.replace(/\.[^.]+$/, "");
    const extra = base.match(/^(lookbook-\d+)-\d+$/i);
    const cardKey = extra ? extra[1] : base;
    if (!cards.has(cardKey)) {
      const meta = content.lookbookCaptions[cardKey] || {};
      cards.set(cardKey, {
        id: cardKey,
        title: meta.title || "",
        client: meta.client || "",
        location: meta.location || "",
        category: meta.category || "",
        details: meta.details || "",
        images: [],
        thumb: null,
      });
    }
    const card = cards.get(cardKey);
    if (extra) card.images.push(lookbookCtx("./" + file));
    else {
      card.images.unshift(lookbookCtx("./" + file));
      card.thumb = getThumb(file);
    }
  });
  return [...cards.values()].map((c) => ({ ...c, image: c.images[0] }));
}

const lookItems = buildLooks();

const rem = lookItems.length % 3;
const lastRowStart = lookItems.length - (rem === 0 ? 3 : rem);
const lastRowOffset = rem === 1 ? " lg:col-start-3" : rem === 2 ? " lg:col-start-2" : "";

export default function RecentLooks() {
  const l = content.looks;
  const [selected, setSelected] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [closing, setClosing] = useState(false);
  const [ready, setReady] = useState({});
  const closeTimer = useRef(null);

  const closeModal = () => {
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setSelected(null);
      setClosing(false);
    }, 280);
  };

  const openLook = (look) => {
    clearTimeout(closeTimer.current);
    setClosing(false);
    setImgIndex(0);
    setPaused(false);
    setSelected(look);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const imgs = selected ? (selected.images && selected.images.length ? selected.images : [selected.image]) : [];
  const stepImg = (d) => setImgIndex((p) => (p + d + imgs.length) % imgs.length);

  useEffect(() => {
    if (!selected || imgs.length < 2 || paused) return;
    const t = setInterval(() => setImgIndex((p) => (p + 1) % imgs.length), 7000);
    return () => clearInterval(t);
  }, [selected, imgs.length, paused]);

  useEffect(() => {
    if (!selected) return;
    const key = `${selected.id}-${imgIndex}`;
    if (ready[key]) return;
    const img = new Image();
    img.onload = () => setReady((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    img.src = imgs[imgIndex];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, imgIndex]);

  useEffect(() => {
    if (!selected || imgs.length < 2) return;
    [1, -1].forEach((d) => {
      const idx = (imgIndex + d + imgs.length) % imgs.length;
      const key = `${selected.id}-${idx}`;
      if (ready[key]) return;
      const img = new Image();
      img.onload = () => setReady((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
      img.src = imgs[idx];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, imgIndex]);

  const currentKey = selected ? `${selected.id}-${imgIndex}` : null;
  const showingFull = selected ? !!ready[currentKey] : false;
  const displaySrc = selected ? (showingFull ? imgs[imgIndex] : (selected.thumb || imgs[imgIndex])) : null;

  return (
    <section id="recent-looks" data-testid="recent-looks-section" className="relative pt-16 lg:pt-24 pb-16 lg:pb-20 bg-sand">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="text-[11px] uppercase tracking-[0.3em] text-gold mb-6"
          >
            {l.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-serif font-light text-4xl sm:text-5xl leading-tight"
            data-testid="looks-heading"
          >
            {l.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="mt-6 text-sm sm:text-base text-slate2 leading-relaxed"
          >
            {l.intro}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 md:gap-5">
          {lookItems.map((look, i) => (
            <button
              key={look.id}
              data-testid={`look-card-${i}`}
              onClick={() => openLook(look)}
              style={{ animationDelay: `${(i % 3) * 0.08}s` }}
              className={`card-fade relative overflow-hidden group text-left cursor-pointer aspect-[2/3] lg:col-span-2${i === lastRowStart ? lastRowOffset : ""}`}
            >
              <img
                src={look.thumb || look.image}
                alt={look.title || "Lookbook photo"}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-500" />
              <div className="absolute inset-x-0 bottom-0 p-5 lg:p-6">
                {look.category && (
                  <p className="text-[10px] uppercase tracking-[0.25em] text-goldsoft mb-2">{look.category}</p>
                )}
                <p className="text-[11px] text-linen/80 uppercase tracking-[0.15em]">
                  {look.client}{look.location ? ` · ${look.location}` : ""}
                </p>
              </div>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-linen/90 text-ink text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                View look
              </div>
            </button>
          ))}
        </div>

        <Spotlight />
      </div>

      {selected && (
        <div
          data-testid="look-modal"
          className={`fixed inset-0 z-[90] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 transition-opacity duration-300 ${closing ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          onClick={closeModal}
        >
          <div
            className="viewer-enter relative inline-block max-w-[94vw] max-h-[92vh] overflow-hidden shadow-2xl leading-none"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={() => setPaused(true)}
            onPointerUp={() => setPaused(false)}
            onPointerLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
          >
            <img
              key={displaySrc}
              src={displaySrc}
              alt={selected.title || "Lookbook photo"}
              className="img-fade block max-h-[92vh] max-w-[94vw] w-auto h-auto object-contain"
            />
            {imgs.length > 1 && (
              <>
                <div className="absolute top-1/2 -translate-y-1/2 inset-x-0 flex justify-between px-3 pointer-events-none">
                  <button
                    data-testid="look-prev-btn"
                    onClick={(e) => { e.stopPropagation(); stepImg(-1); }}
                    className="w-10 h-10 bg-ink/60 backdrop-blur text-linen flex items-center justify-center rounded-full hover:bg-ink transition-colors pointer-events-auto"
                    aria-label="Previous photo"
                  >
                    <ArrowLeft size={15} />
                  </button>
                  <button
                    data-testid="look-next-btn"
                    onClick={(e) => { e.stopPropagation(); stepImg(1); }}
                    className="w-10 h-10 bg-ink/60 backdrop-blur text-linen flex items-center justify-center rounded-full hover:bg-ink transition-colors pointer-events-auto"
                    aria-label="Next photo"
                  >
                    <ArrowRight size={15} />
                  </button>
                </div>
                <div className="absolute top-4 left-4 bg-ink/60 backdrop-blur text-linen text-[10px] tracking-[0.2em] px-3 py-1.5 rounded-full">
                  {imgIndex + 1} / {imgs.length}
                </div>
              </>
            )}
            {(selected.category || selected.client) && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 via-ink/55 to-transparent px-5 sm:px-7 pt-12 pb-4 sm:pb-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-goldsoft truncate" data-testid="look-modal-title">{selected.category}</p>
                <p className="mt-1 text-[11px] text-linen/85 uppercase tracking-[0.15em] flex items-center gap-2 min-w-0">
                  <MapPin size={11} className="text-crimson shrink-0" />
                  <span className="truncate">{selected.client}{selected.location ? ` · ${selected.location}` : ""}</span>
                </p>
              </div>
            )}
            <button
              data-testid="look-modal-close"
              onClick={closeModal}
              className="absolute top-3 right-3 w-10 h-10 bg-linen text-ink flex items-center justify-center hover:bg-goldsoft transition-colors duration-300 z-10"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
